import type { AxiosError } from 'axios';
import type { VoProfiles, VoSiteProfile } from '@/apis/profile';
import type { RouteLocationNormalized } from 'vue-router';
import { isArray, omit, pick } from 'radash';
import { computed, reactive, toRefs } from 'vue';
import { defineStore } from 'pinia';
import {
  DEFAULT_TITLE_TEMPLATE,
  IS_JYH_PROD,
  IS_JYH_RESERVE,
  IS_ZJ_PROD,
  STORE_PROFILE,
} from '@inn/const';
import { isEmpty } from '@hlx/use';
import { useFavicon, useTitle } from '@vueuse/core';
import defaultHlxLogoPure from '@inn/assets/logo/logo-hlx-pure.png';
import defaultZjLogoPure from '@inn/assets/logo/logo-zj-pure.png';
import defaultJyhLogoPure from '@inn/assets/logo/logo-jyh-pure.png';
import { usePermitStore } from './permits';
import { getProfiles, getFeaturesByDomain, patchMySite } from '@/apis/profile';
import { isXomError, XomError } from '@/utils/error';
import { useNotifySdk } from '@/hooks';

let logoPure = defaultHlxLogoPure;
if (IS_ZJ_PROD) {
  logoPure = defaultZjLogoPure;
} else if (IS_JYH_PROD || IS_JYH_RESERVE) {
  logoPure = defaultJyhLogoPure;
}

const LOGO_BASE64_MIMES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  ico: 'image/x-icon',
  svg: 'image/svg+xml',
};

/**
 * 归一化 logo 资源，兼容子系统传入的多种格式：
 * - URL / data URI / 原生 `<svg>` 字符串 → 原样返回
 * - 裸 base64（仅 base64 字符集）→ 依据 `type` 补 `data:image/...;base64,` 前缀（默认 png）
 * - 其它（如相对/绝对路径）→ 原样返回
 */
function normalizeLogoSource(source: string, type?: string): string {
  if (!source) {
    return source;
  }
  const trimmed = source.trim();

  if (
    /^(https?:)?\/\//i.test(trimmed) ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('<svg')
  ) {
    return source;
  }

  if (/^[A-Za-z0-9+/]+={0,2}$/.test(trimmed)) {
    const key = (type || 'png').toLowerCase();
    const mime = LOGO_BASE64_MIMES[key] || `image/${key}`;
    return `data:${mime};base64,${trimmed}`;
  }

  return source;
}

export const useProfileStore = defineStore(
  'profile',
  () => {
    const favicon = useFavicon();
    const permits = usePermitStore();
    const titleSetter = useTitle();

    const states = reactive<VoProfiles>({
      id: null,
      defaultPwd: true,
      account: null,
      telephone: null,
      email: null,
      name: null,
      avatar: null,
      description: null,
      type: null,
      gender: null,
      status: null,
      site: null,
      org: null,
      roles: null,
      assetWhitelist: null,
      preference: null,
      passwordResetType: null,
      bornAppId: null,
      configVoBusinessV3: null,
    });

    const enabled = computed(() => states.status === 'ENABLE');
    const root = computed(() => states.type === 'SYSTEM');
    const admins = computed(() => states.type === 'SUPER');
    const authed = computed(() => states.status === 'ENABLE' && !!states.site?.id);
    const nickname = computed(() =>
      typeof states.name === 'string' ? states.name.trim() || states.account : states.account,
    );

    const key2ActiveRuleMap = computed(() =>
      permits.systems.reduce(
        (map, cur) => (map.set(cur.key, cur.activeRule), map),
        new Map<string, string>(),
      ),
    );
    const path2LabelMap = computed(() =>
      permits.modules.reduce((map, cur) => {
        const activeRule = key2ActiveRuleMap.value.get(cur.system?.key);
        if (activeRule) {
          map.set(`${activeRule}${cur.path}`, cur.label);
        }
        return map;
      }, new Map<string, string>()),
    );

    const setFavicon = () => {
      const iconUrl = states.site?.logoPure;
      if (!iconUrl) {
        favicon.value = logoPure;
        return;
      }

      const iconLoader = document.createElement('img');
      iconLoader.addEventListener('load', () => {
        favicon.value = iconUrl;
        iconLoader.remove();
      });
      iconLoader.addEventListener('error', () => {
        favicon.value = logoPure;
        iconLoader.remove();
      });
      iconLoader.src = iconUrl;
    };

    const setTitle = (currentRoute: RouteLocationNormalized) => {
      const routeLabel = path2LabelMap.value.get(currentRoute.path);
      const title = routeLabel || (IS_ZJ_PROD ? '工作台' : '融媒体中台');
      const template = (states.site?.titleTemplate as unknown) || DEFAULT_TITLE_TEMPLATE;
      if (!template || typeof template !== 'string' || !/{\S*}/.test(template)) {
        return;
      }

      const [, defaultTitle] = template.match(/{([^$]*)}/) || [];
      const newly = typeof title === 'string' && !isEmpty(title) ? title : defaultTitle || '';
      titleSetter.value = template.replace(/{.*}/, newly);
    };

    const confSite = async () => {
      try {
        const infos = await getFeaturesByDomain();
        const truly = isArray(infos) ? findAndEnsureSite(infos) : infos;
        if (truly) {
          states.site = {
            ...states.site,
            ...pick(truly, ['logoPure', 'logoText', 'titleTemplate']),
          };
        }
      } catch (error) {
        console.error(error);
      } finally {
        setFavicon();
      }
    };

    const reload = async () => {
      let infos: VoProfiles;

      try {
        infos = await getProfiles();
      } catch (error) {
        clear();
        if (isXomError(error)) {
          throw error;
        }
        throw new XomError({ code: 'err_profile_load' });
      }

      if (!infos.id || !infos.site?.id || !infos.org?.id || !infos.account || !infos.type) {
        clear();
        throw new XomError({ code: 'err_invalid_profile' });
      }
      if (infos.type === 'NORMAL' && !infos.roles?.length) {
        clear();
        throw new XomError({ code: 'err_invalid_role' });
      }
      if (!infos.status || infos.status !== 'ENABLE') {
        clear();
        throw new XomError({ code: 'err_profile_locked' });
      }

      const omitKeys: (keyof VoSiteProfile)[] = ['logoPure', 'logoText'];
      const site = omit(infos.site, omitKeys);
      Object.assign(states, { ...infos, site, bornAppId: infos.bornAppId || null });
      setFavicon();
    };

    const toggleSite = async (site: BaseRelates) => {
      try {
        await patchMySite(site.id);
      } catch (error) {
        throw new XomError(error as AxiosError);
      }

      // TODO regenerate authed routes
      await reload();

      permits.clear();
      await permits.reload();

      // 通知其他窗口刷新页面
      useNotifySdk().postMessage({ type: 'CLIENT_TOGGLE_SITE_SIGNAL' });
    };

    const validAsset = (url: string) => {
      return Array.isArray(states.assetWhitelist) && states.assetWhitelist.includes(url);
    };

    const replaceLogo: XomStateReplaceLogo = (logo) => {
      if (!states.site) {
        return;
      }
      const { logoPure: nextPure, logoText: nextText, logoPureType, logoTextType } = logo ?? {};
      if (typeof nextPure === 'string') {
        states.site.logoPure = normalizeLogoSource(nextPure, logoPureType);
      }
      if (typeof nextText === 'string') {
        states.site.logoText = normalizeLogoSource(nextText, logoTextType);
      }
      setFavicon();
    };

    const clear = () => {
      localStorage.removeItem(STORE_PROFILE);
      // this.$reset();
    };

    return {
      ...toRefs(states),
      authed,
      enabled,
      admins,
      root,
      nickname,
      setFavicon,
      setTitle,
      confSite,
      reload,
      toggleSite,
      validAsset,
      replaceLogo,
      clear,
    };
  },
  {
    persist: {
      key: STORE_PROFILE,
    },
  },
);

export function findAndEnsureSite<T extends VoSiteProfile>(infos: T[]) {
  const isTruly = (info: T) => {
    return info.id && info.name && info.cmsDomain && info.cmsDomain.includes(location.host);
  };
  return infos.find(isTruly);
}
